import { lazy, Suspense, useState, useEffect } from 'react'
import { AuthChangeRedirector, AnonymousRoute, AuthenticatedRoute } from './auth'
import {
  createBrowserRouter,
  RouterProvider
} from 'react-router-dom'
import Login from './account/Login'
import Logout from './account/Logout'
import ProviderCallback from './socialaccount/ProviderCallback'
import Root from './Root'
import RouteError from './RouteError'
import StaffRoute from './bom/StaffRoute'
import { ProjectSelectionProvider } from './carbon/ProjectSelection'

const CarbonFootprintLibrary = lazy(() => import('./carbon/CarbonFootprintLibrary'))
const DrivingForces = lazy(() => import('./sustainability/DrivingForces'))
const CaseStudies = lazy(() => import('./sustainability/CaseStudies'))
const Home = lazy(() => import('./Home'))
const BomValidator = lazy(() => import('./bom/BomValidator'))
const ItemLibraryPage = lazy(() => import('./carbon/ItemLibrary'))
const MyProfilePage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.MyProfilePage })))
const CarbonCalculatorCreatePage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.CarbonCalculatorCreatePage })))
const GroupedProjectsPage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.GroupedProjectsPage })))
const ProjectBomVersionPage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.ProjectBomVersionPage })))
const ProjectCreatePage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.ProjectCreatePage })))
const ProjectDetailPage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.ProjectDetailPage })))
const ParentProjectDetail = lazy(() => import('./carbon/ParentProjectDetail'))
const ProjectListPage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.ProjectListPage })))
const ProjectReportPage = lazy(() => import('./carbon/CarbonWorkflow').then((module) => ({ default: module.ProjectReportPage })))

function routeSuspense (element) {
  return <Suspense fallback={<main className='carbon-workspace'><p>Loading...</p></main>}>{element}</Suspense>
}

function authenticatedRoute (element) {
  return <AuthenticatedRoute>{routeSuspense(element)}</AuthenticatedRoute>
}

function createRouter () {
  return createBrowserRouter([
    {
      path: '/search/carbon-footprint-library',
      element: <AuthChangeRedirector>{authenticatedRoute(<CarbonFootprintLibrary />)}</AuthChangeRedirector>
    },
    {
      path: '/driving-forces-sustainability',
      element: <AuthChangeRedirector>{authenticatedRoute(<DrivingForces />)}</AuthChangeRedirector>
    },
    {
      path: '/sustainable-product-case-studies',
      element: <AuthChangeRedirector>{authenticatedRoute(<CaseStudies />)}</AuthChangeRedirector>
    },
    {
      path: '/',
      element: <AuthChangeRedirector><ProjectSelectionProvider><Root /></ProjectSelectionProvider></AuthChangeRedirector>,
      children: [
        {
          path: '/',
          element: authenticatedRoute(<Home />)
        },
        {
          path: '/bom',
          element: authenticatedRoute(<StaffRoute><BomValidator /></StaffRoute>)
        },
        {
          path: '/item-library',
          element: authenticatedRoute(<ItemLibraryPage />)
        },
        {
          path: '/projects',
          element: authenticatedRoute(<ProjectListPage />)
        },
        {
          path: '/projects/grouped',
          element: authenticatedRoute(<GroupedProjectsPage />)
        },
        {
          path: '/projects/folders/:folderId',
          element: authenticatedRoute(<ParentProjectDetail />)
        },
        {
          path: '/projects/new',
          element: authenticatedRoute(<ProjectCreatePage />)
        },
        {
          path: '/carbon-calculator/new',
          element: authenticatedRoute(<CarbonCalculatorCreatePage />)
        },
        {
          path: '/projects/:projectId',
          element: authenticatedRoute(<ProjectDetailPage />)
        },
        {
          path: '/projects/:projectId/workflow/:stepSlug',
          element: authenticatedRoute(<ProjectDetailPage />)
        },
        {
          path: '/projects/:projectId/calculator/:stepSlug',
          element: authenticatedRoute(<ProjectDetailPage />)
        },
        {
          path: '/projects/:projectId/bom-version',
          element: authenticatedRoute(<ProjectBomVersionPage />)
        },
        {
          path: '/projects/:projectId/report',
          element: authenticatedRoute(<ProjectReportPage />)
        },
        {
          path: '/projects/:projectId/report/versions/:versionId',
          element: authenticatedRoute(<ProjectReportPage />)
        },
        {
          path: '/account/login',
          element: <AnonymousRoute><Login /></AnonymousRoute>
        },
        {
          path: '/account/profile',
          element: authenticatedRoute(<MyProfilePage />)
        },
        {
          path: '/account/logout',
          element: <AuthenticatedRoute><Logout /></AuthenticatedRoute>
        },
        {
          path: '/account/provider/callback',
          element: <ProviderCallback />
        }
      ]
    },
    {
      path: '*',
      element: <RouteError notFound />
    }
  ].map(route => ({ ...route, errorElement: <RouteError /> })))
}

export default function Router () {
  // If we create the router globally, the loaders of the routes already trigger
  // even before the <AuthContext/> trigger the initial loading of the auth.
  // state.
  const [router, setRouter] = useState(null)
  useEffect(() => {
    setRouter(createRouter())
  }, [])
  return router ? <RouterProvider router={router} /> : null
}
